import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  GridFrame,
  GridScroll,
  LoadingState,
  StatusChip,
  Table,
  Td,
  TextArea,
  Th,
  Tr,
  statusTone,
} from '@tessma/design-system';
import { ApiError, type ActivityEntry, type WorkGateway } from './gateway.js';
import type {
  ApprovalDetail,
  ApprovalSummary,
  CommentEntry,
  TaskDetail,
  TaskSummary,
} from './gateway.js';

/**
 * The four Stage 5 screens, composed ENTIRELY from the frozen design system.
 *
 * Not one new visual component, not one new colour, not one hand-written
 * measurement. Every surface is `Card`, every list is the frozen grid, every
 * refusal is `EmptyState`/`ErrorState`, and every status is `StatusChip` with
 * the frozen `statusTone` mapping. Where a screen needed something the system
 * does not have, the answer was to use what it does have rather than to invent
 * — which is what "frozen" means in practice, and is the only reason a later
 * stage can restyle the product by changing tokens.
 *
 * WHAT THE UI DOES NOT DO IS DECIDE ANYTHING. Buttons are hidden when the
 * session says the permission is absent, and that is a courtesy, not a control:
 * the server checks again, and a caller who reconstructs the request by hand
 * gets the same 403. UI hiding is not security, and the code says so where
 * somebody might otherwise believe it.
 */

function useAsync<T>(load: () => Promise<T>, deps: readonly unknown[]) {
  const [state, setState] = useState<
    { status: 'loading' } | { status: 'ready'; value: T } | { status: 'failed'; error: ApiError }
  >({ status: 'loading' });

  const run = useCallback(() => {
    setState({ status: 'loading' });
    load().then(
      (value) => {
        setState({ status: 'ready', value });
      },
      (error: unknown) => {
        setState({
          status: 'failed',
          error:
            error instanceof ApiError
              ? error
              : new ApiError(0, 'Could not reach the server', 'Check your connection and retry.'),
        });
      },
    );
    // `load` is a fresh closure every render, so it cannot be the dependency —
    // depending on it would reload on every render forever. The list the CALLER
    // passes is the real one: what the request actually varies with.
  }, deps);

  useEffect(run, [run]);
  return { state, reload: run };
}

function when(iso: string | null): string {
  if (iso === null) return '—';
  // The session's locale is en-GB and the shell is built for it; a date
  // rendered by the browser's own formatter follows the reader's settings,
  // which is what somebody reading their own task list expects.
  return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}

/** Overdue is a fact about the row, not a status the server stores. */
function overdue(task: TaskSummary): boolean {
  return task.dueAt !== null && new Date(task.dueAt).getTime() < Date.now();
}

// ---- My Tasks ---------------------------------------------------------------

export function MyTasksScreen({
  gateway,
  onOpen,
}: {
  readonly gateway: WorkGateway;
  readonly onOpen: (taskId: string) => void;
}) {
  const { state, reload } = useAsync<readonly TaskSummary[]>(() => gateway.myTasks(), [gateway]);

  if (state.status === 'loading') return <LoadingState title="Loading your tasks…" />;
  if (state.status === 'failed') {
    return (
      <ErrorState title={state.error.message} description={state.error.detail} onRetry={reload} />
    );
  }
  if (state.value.length === 0) {
    return (
      <Card>
        <EmptyState
          icon="check-circle"
          title="Nothing is waiting for you"
          description="Work assigned to you, or to a role you hold, appears here."
        />
      </Card>
    );
  }

  return (
    <Card padding="flush">
      <GridFrame>
        <GridScroll>
          <Table label="My tasks">
            <thead>
              <Tr>
                <Th>Task</Th>
                <Th>Raised by</Th>
                <Th>Priority</Th>
                <Th>Due</Th>
                <Th>Status</Th>
              </Tr>
            </thead>
            <tbody>
              {state.value.map((task) => (
                <Tr key={task.id}>
                  <Td>
                    <Button
                      variant="tertiary"
                      onClick={() => {
                        onOpen(task.id);
                      }}
                    >
                      {task.title}
                    </Button>
                  </Td>
                  <Td variant="secondary">{task.sourceModule}</Td>
                  <Td variant="secondary">{task.priority}</Td>
                  <Td variant="secondary">{when(task.dueAt)}</Td>
                  <Td>
                    <StatusChip tone={overdue(task) ? 'danger' : statusTone(task.status)}>
                      {overdue(task) ? 'Overdue' : task.status}
                    </StatusChip>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </GridScroll>
      </GridFrame>
    </Card>
  );
}

// ---- one task ---------------------------------------------------------------

export function TaskDetailScreen({
  gateway,
  taskId,
  canComplete,
  canComment,
}: {
  readonly gateway: WorkGateway;
  readonly taskId: string;
  readonly canComplete: boolean;
  readonly canComment: boolean;
}) {
  const { state, reload } = useAsync<TaskDetail>(() => gateway.task(taskId), [gateway, taskId]);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<ApiError | undefined>(undefined);

  const complete = useCallback(() => {
    setPending(true);
    setFailure(undefined);
    // ONE KEY PER ACTION. A double-clicked button sends the same key twice and
    // the second request replays the first's answer rather than completing the
    // task again.
    const key = gateway.key();
    gateway.completeTask(taskId, key).then(
      () => {
        setPending(false);
        reload();
      },
      (error: unknown) => {
        setPending(false);
        if (error instanceof ApiError) setFailure(error);
      },
    );
  }, [gateway, taskId, reload]);

  if (state.status === 'loading') return <LoadingState title="Loading the task…" />;
  if (state.status === 'failed') {
    return (
      <ErrorState title={state.error.message} description={state.error.detail} onRetry={reload} />
    );
  }

  const { task } = state.value;
  const settled = task.status === 'completed' || task.status === 'cancelled';

  return (
    <>
      {failure !== undefined && (
        <Alert tone="danger" title={failure.message}>
          {failure.detail}
        </Alert>
      )}
      <Card>
        <CardHeader
          title={task.title}
          subtitle={`${task.sourceModule} · raised ${when(task.createdAt)}`}
          action={
            // HIDDEN, NOT DISABLED, when the permission is absent: a disabled
            // control advertises a capability. And this is a COURTESY — the
            // server checks again and refuses a hand-made request identically.
            canComplete && !settled ? (
              <Button onClick={complete} disabled={pending}>
                {pending ? 'Completing…' : 'Mark as done'}
              </Button>
            ) : (
              <StatusChip tone={statusTone(task.status)}>{task.status}</StatusChip>
            )
          }
        />
        {task.description !== '' && <p>{task.description}</p>}
      </Card>

      <CollaborationPanel
        gateway={gateway}
        resource="platform.task"
        recordId={taskId}
        canComment={canComment}
        activity={state.value.activity}
      />
    </>
  );
}

// ---- the approval inbox -----------------------------------------------------

export function ApprovalInboxScreen({
  gateway,
  onOpen,
}: {
  readonly gateway: WorkGateway;
  readonly onOpen: (instanceId: string) => void;
}) {
  const { state, reload } = useAsync<readonly ApprovalSummary[]>(
    () => gateway.approvals(),
    [gateway],
  );

  if (state.status === 'loading') return <LoadingState title="Loading your approvals…" />;
  if (state.status === 'failed') {
    return (
      <ErrorState title={state.error.message} description={state.error.detail} onRetry={reload} />
    );
  }
  if (state.value.length === 0) {
    return (
      <Card>
        <EmptyState
          icon="check-circle"
          title="Nothing is waiting for your decision"
          description="Approvals you have been asked to decide appear here."
        />
      </Card>
    );
  }

  return (
    <Card padding="flush">
      <GridFrame>
        <GridScroll>
          <Table label="Approval inbox">
            <thead>
              <Tr>
                <Th>Step</Th>
                <Th>Subject</Th>
                <Th>Asked</Th>
                <Th>Due</Th>
              </Tr>
            </thead>
            <tbody>
              {state.value.map((approval) => (
                <Tr key={approval.approvalId}>
                  <Td>
                    <Button
                      variant="tertiary"
                      onClick={() => {
                        onOpen(approval.instanceId);
                      }}
                    >
                      {approval.stepName}
                    </Button>
                  </Td>
                  <Td variant="secondary">{approval.subjectResource}</Td>
                  <Td variant="secondary">{when(approval.openedAt)}</Td>
                  <Td>
                    {approval.dueAt !== null && new Date(approval.dueAt).getTime() < Date.now() ? (
                      <StatusChip tone="danger">Overdue</StatusChip>
                    ) : (
                      <span>{when(approval.dueAt)}</span>
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </GridScroll>
      </GridFrame>
    </Card>
  );
}

// ---- one approval -----------------------------------------------------------

export function ApprovalDetailScreen({
  gateway,
  instanceId,
  canDecide,
  canComment,
}: {
  readonly gateway: WorkGateway;
  readonly instanceId: string;
  readonly canDecide: boolean;
  readonly canComment: boolean;
}) {
  const { state, reload } = useAsync<ApprovalDetail>(
    () => gateway.approval(instanceId),
    [gateway, instanceId],
  );
  const [comment, setComment] = useState('');
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<ApiError | undefined>(undefined);

  const decide = useCallback(
    (decision: 'approved' | 'rejected' | 'sent_back', stepInstanceId: string | undefined) => {
      setPending(true);
      setFailure(undefined);
      const key = gateway.key();
      gateway
        .decide(
          instanceId,
          {
            decision,
            ...(comment.trim() === '' ? {} : { comment: comment.trim() }),
            // THE STEP THE PERSON BELIEVES THEY ARE DECIDING. Without it, an
            // approver whose step was decided a moment earlier is told "you
            // were not asked to decide this step" — true of whatever is open
            // NOW and completely misleading about what happened to theirs.
            ...(stepInstanceId === undefined ? {} : { expectedStepInstanceId: stepInstanceId }),
          },
          key,
        )
        .then(
          () => {
            setPending(false);
            setComment('');
            reload();
          },
          (error: unknown) => {
            setPending(false);
            if (error instanceof ApiError) setFailure(error);
          },
        );
    },
    [gateway, instanceId, comment, reload],
  );

  if (state.status === 'loading') return <LoadingState title="Loading the approval…" />;
  if (state.status === 'failed') {
    return (
      <ErrorState title={state.error.message} description={state.error.detail} onRetry={reload} />
    );
  }

  const { instance, steps, history } = state.value;
  const open = steps.find((step) => step.state === 'pending');
  const settled = instance.state !== 'running';

  return (
    <>
      {failure !== undefined && (
        <Alert tone="danger" title={failure.message}>
          {failure.detail}
        </Alert>
      )}

      <Card>
        <CardHeader
          title={`${instance.subjectResource} ${instance.subjectRecordId.slice(0, 8)}`}
          subtitle={`Workflow ${instance.state}`}
          action={<StatusChip tone={statusTone(instance.state)}>{instance.state}</StatusChip>}
        />

        {canDecide && !settled && open !== undefined && (
          <>
            <TextArea
              label="Comment"
              helper="Optional, and kept with the decision. Say why, not what."
              value={comment}
              onChange={(event) => {
                setComment(event.target.value);
              }}
            />
            <div style={{ display: 'flex', gap: 'var(--space-8)' }}>
              <Button
                onClick={() => {
                  decide('approved', open.stepInstanceId);
                }}
                disabled={pending}
              >
                Approve
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  decide('sent_back', open.stepInstanceId);
                }}
                disabled={pending}
              >
                Send back
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  decide('rejected', open.stepInstanceId);
                }}
                disabled={pending}
              >
                Reject
              </Button>
            </div>
          </>
        )}
      </Card>

      <Card padding="flush">
        <GridFrame>
          <GridScroll>
            <Table label="Steps and decisions">
              <thead>
                <Tr>
                  <Th>Step</Th>
                  <Th>State</Th>
                  <Th>Decisions</Th>
                </Tr>
              </thead>
              <tbody>
                {steps.map((step) => (
                  <Tr key={step.stepInstanceId}>
                    <Td>{step.stepNumber}</Td>
                    <Td>
                      <StatusChip tone={statusTone(step.state)}>{step.state}</StatusChip>
                    </Td>
                    <Td variant="secondary">
                      {step.decisions.length === 0
                        ? '—'
                        : step.decisions
                            .map(
                              (decision) =>
                                `${decision.approverUserId.slice(0, 8)}: ${decision.decision}`,
                            )
                            .join(', ')}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </GridScroll>
        </GridFrame>
      </Card>

      <Card>
        <CardHeader
          title="History"
          subtitle="Append-only in the database. Nothing shown here can have been edited."
        />
        <ol>
          {history.map((entry) => (
            <li key={entry.id}>
              {entry.action} — {when(entry.occurredAt)}
              {entry.actorType === 'system' && ' (automatic)'}
            </li>
          ))}
        </ol>
      </Card>

      <CollaborationPanel
        gateway={gateway}
        resource="platform.workflow_instance"
        recordId={instanceId}
        canComment={canComment}
        activity={state.value.activity}
      />
    </>
  );
}

// ---- comments and activity, on any record ----------------------------------

export function CollaborationPanel({
  gateway,
  resource,
  recordId,
  canComment,
  activity,
}: {
  readonly gateway: WorkGateway;
  readonly resource: string;
  readonly recordId: string;
  readonly canComment: boolean;
  readonly activity: readonly ActivityEntry[];
}) {
  const { state, reload } = useAsync<readonly CommentEntry[]>(
    () => gateway.comments(resource, recordId),
    [gateway, resource, recordId],
  );
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState(false);
  const [unresolved, setUnresolved] = useState<readonly string[]>([]);
  const [failure, setFailure] = useState<ApiError | undefined>(undefined);

  const send = useCallback(() => {
    if (draft.trim() === '') return;
    setPending(true);
    setFailure(undefined);
    gateway.comment(resource, recordId, draft.trim(), gateway.key()).then(
      (result) => {
        setPending(false);
        setDraft('');
        // Reported rather than treated as an error. A handle naming somebody in
        // ANOTHER tenant resolves to nobody here, and saying which is which
        // would be the disclosure the isolation exists to prevent.
        setUnresolved(result.unresolvedHandles);
        reload();
      },
      (error: unknown) => {
        setPending(false);
        if (error instanceof ApiError) setFailure(error);
      },
    );
  }, [gateway, resource, recordId, draft, reload]);

  return (
    <Card>
      <CardHeader title="Comments and activity" />

      {failure !== undefined && (
        <Alert tone="danger" title={failure.message}>
          {failure.detail}
        </Alert>
      )}
      {unresolved.length > 0 && (
        <Alert tone="warning" title="Some names did not match anyone here">
          {unresolved.map((handle) => `@${handle}`).join(', ')} — nobody in this organisation uses
          that name, so they have not been told.
        </Alert>
      )}

      {state.status === 'loading' && <LoadingState compact title="Loading comments…" />}
      {state.status === 'failed' && (
        <ErrorState title={state.error.message} description={state.error.detail} onRetry={reload} />
      )}
      {state.status === 'ready' && state.value.length === 0 && (
        <EmptyState
          icon="list"
          title="No comments yet"
          description="Mention a colleague with @ and they will be told."
        />
      )}
      {state.status === 'ready' && state.value.length > 0 && (
        <ol>
          {state.value.map((entry) => (
            <li key={entry.id}>
              <strong>{entry.authorId.slice(0, 8)}</strong> · {when(entry.createdAt)}
              {/* The row survives so replies keep their referent; the words do not. */}
              <p>{entry.deleted ? <em>This comment was deleted.</em> : entry.body}</p>
            </li>
          ))}
        </ol>
      )}

      {canComment && (
        <>
          <TextArea
            label="Add a comment"
            helper="Mention a colleague with @ and they will be told."
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
          />
          <Button onClick={send} disabled={pending || draft.trim() === ''}>
            {pending ? 'Posting…' : 'Comment'}
          </Button>
        </>
      )}

      {activity.length > 0 && (
        <>
          <CardHeader title="Activity" />
          <ol>
            {activity.map((entry) => (
              <li key={entry.id}>
                {entry.activityType} — {when(entry.occurredAt)}
                {/* No actor means the clock did it, not an anonymous person. */}
                {entry.origin === 'system' && ' (automatic)'}
              </li>
            ))}
          </ol>
        </>
      )}
    </Card>
  );
}
